'use client';

import { useRef, useState, useEffect, type ReactNode } from 'react';

interface LazySectionProps {
  children: ReactNode;
  /** Minimum height for the skeleton placeholder */
  minHeight?: string;
  /** Root margin for triggering load before section is visible */
  rootMargin?: string;
}

function SectionSkeleton({ minHeight }: { minHeight: string }) {
  return (
    <div className="animate-pulse space-y-4 px-4 py-12" style={{ minHeight }}>
      <div className="mx-auto h-8 w-2/3 rounded-md bg-gray-200" />
      <div className="mx-auto h-4 w-1/2 rounded-md bg-gray-200" />
      <div className="mx-auto mt-8 grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-48 rounded-lg bg-gray-200" />
        ))}
      </div>
    </div>
  );
}

export function LazySection({
  children,
  minHeight = '300px',
  rootMargin = '200px',
}: LazySectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return <div ref={ref}>{isVisible ? children : <SectionSkeleton minHeight={minHeight} />}</div>;
}
